import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { esignApi } from "../../services/api";

interface Props {
  open: boolean;
  onClose: () => void;
  onVoided: () => void;
  envelopeId: string;
  envelopeSubject: string;
}

const VoidEnvelopeModal: React.FC<Props> = ({
  open,
  onClose,
  onVoided,
  envelopeId,
  envelopeSubject,
}) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await esignApi.voidEnvelope(envelopeId, reason.trim());
      setReason("");
      onVoided();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to void request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setReason("");
    setError("");
    onClose();
  };

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title="Void Request"
      size="sm"
      icon={AlertTriangle}
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason.trim() || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "Voiding..." : "Void Request"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            This will cancel the signing request for{" "}
            <span className="font-semibold">'{envelopeSubject}'</span> and notify all
            signers.
          </p>
        </div>

        {/* Reason textarea */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Please provide a reason for voiding
            <span className="text-red-500 ml-0.5">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Terms have changed, incorrect document attached..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            disabled={submitting}
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    </PremiumModal>
  );
};

export default VoidEnvelopeModal;
